import { EventHandler } from '@paradigm/utils/src/createObservable';
import { GrfqQuote, GrfqQuotes } from '../entities/grfq/domain';
import EntityStore from './entity';

/**
 * Sort quotes descending or ascending by price. If price is the same, sort quotes
 * ascending by date.
 *
 * @param priceOrder 'asc' | 'desc'
 * @returns Ask or Bid comparator
 */
function createComparator(priceOrder: 'asc' | 'desc') {
  return function comparator(quoteA: GrfqQuote, quoteB: GrfqQuote): number {
    const priceDiff =
      priceOrder === 'asc'
        ? Number.parseFloat(quoteA.price) - Number.parseFloat(quoteB.price)
        : Number.parseFloat(quoteB.price) - Number.parseFloat(quoteA.price);

    return (
      priceDiff ||
      quoteA.last_activity.getTime() - quoteB.last_activity.getTime()
    );
  };
}

export default class GrfqAsksBidsStore {
  private bidsStore: EntityStore<GrfqQuote>;
  private asksStore: EntityStore<GrfqQuote>;

  constructor() {
    this.bidsStore = new EntityStore<GrfqQuote>({
      sortComparer: createComparator('desc'),
    });
    this.asksStore = new EntityStore<GrfqQuote>({
      sortComparer: createComparator('asc'),
    });
  }

  upsertQuotes({ bids, asks }: GrfqQuotes) {
    this.bidsStore.upsertMany(bids);
    this.asksStore.upsertMany(asks);
  }

  getRfqQuotes() {
    return {
      bids: this.bidsStore.selectAll(),
      asks: this.asksStore.selectAll(),
    };
  }

  selectQuoteById(quoteId: GrfqQuote['id']) {
    return (
      this.bidsStore.selectById(quoteId) || this.asksStore.selectById(quoteId)
    );
  }

  updateQuote(quote: GrfqQuote) {
    if (quote.side === 'BUY') {
      this.bidsStore.updateOne(quote.id, quote);
      return;
    }

    this.asksStore.updateOne(quote.id, quote);
  }

  removeQuote(quote: GrfqQuote) {
    if (quote.side === 'BUY') {
      this.bidsStore.removeOne(quote.id);
      return;
    }

    this.asksStore.removeOne(quote.id);
  }

  clear() {
    this.bidsStore.clear();
    this.asksStore.clear();
  }

  subscribeQuotes(handler: EventHandler<GrfqQuotes>) {
    const handleUpdates = async () => handler(this.getRfqQuotes());

    const unsubBids = this.bidsStore.subscribeList(handleUpdates);
    const unsubAsks = this.asksStore.subscribeList(handleUpdates);

    return () => {
      unsubBids();
      unsubAsks();
    };
  }
}
